import $ from 'jquery';
import {
  GUIDE_EMBED_CHAT_MESSAGE_TYPE,
  GUIDE_EMBED_GO_BACK_MESSAGE_TYPE,
  GUIDE_EMBED_OPEN_URL_MESSAGE_TYPE,
  GUIDE_EMBED_URL,
  GUIDE_SURFACE_PRIVATE_NAVBAR,
  GUIDE_TRACK_EVENT_NAMES,
  GUIDE_WIDTH_STORAGE_KEY,
} from './config';
import {
  normalizeGuideNavigationUrl,
  parseGuideEmbedMessage,
} from './embedMessages';
import { getTopicSlugFromEmbedUrl } from './helpTopics';
import { trackEvent } from '~src/tracking/trackEvent';
import triggerChatFlow from '~src/utils/triggerChatFlow';

const PANEL_ID = 'guidePanel';
const OPEN_CLASS = 'guide-panel-open';
const EXPANDED_CLASS = 'guide-panel-expanded';

const getGuideTitle = () => 'Guide';

const getGuideFrame = () =>
  $(`#${PANEL_ID} iframe[data-guide-frame]`)[0] || null;

const getGuideFrameWindow = () => {
  const iframe = getGuideFrame();
  return iframe ? iframe.contentWindow : null;
};

const getGuideSurface = () => {
  const iframe = getGuideFrame();
  const surface = iframe ? $(iframe).data('guideSurface') : null;
  return surface || GUIDE_SURFACE_PRIVATE_NAVBAR;
};

const isGuidePanelOpen = () => $('body').hasClass(OPEN_CLASS);

const trackGuide = (event, extra = {}) => {
  const eventName = GUIDE_TRACK_EVENT_NAMES[event];
  if (!eventName) return;

  const surface = getGuideSurface();
  const iframe = getGuideFrame();
  const embedUrl = (iframe && iframe.getAttribute('src')) || GUIDE_EMBED_URL;

  trackEvent(eventName, {
    surface,
    shell: 'giotto',
    entry_point: surface.replace(/^giotto_/, ''),
    embed_url: embedUrl,
    topic_slug: getTopicSlugFromEmbedUrl(embedUrl) || 'home',
    ...extra,
  });
};

const openGuideExternalUrl = (url) => {
  const normalizedUrl = normalizeGuideNavigationUrl(url);
  if (!normalizedUrl) return;

  const openedWindow = window.open(
    normalizedUrl,
    '_blank',
    'noopener,noreferrer'
  );
  if (openedWindow) openedWindow.opener = null;
};

/** L'iframe e' cross-origin: il "torna indietro" lo esegue il parent. */
const navigateGuideFrame = (fallbackUrl) => {
  const iframe = getGuideFrame();
  if (!iframe) return;

  iframe.src = normalizeGuideNavigationUrl(fallbackUrl) || GUIDE_EMBED_URL;
};

const bindGuideMessageListener = () => {
  if (window.__giottoGuideEmbedListenerBound) return;

  window.__giottoGuideEmbedListenerBound = true;
  window.addEventListener('message', (event) => {
    const message = parseGuideEmbedMessage(event, getGuideFrameWindow());
    if (!message) return;

    if (message.type === GUIDE_EMBED_CHAT_MESSAGE_TYPE) {
      trackGuide('chat_opened');
      triggerChatFlow();
      return;
    }

    if (message.type === GUIDE_EMBED_OPEN_URL_MESSAGE_TYPE) {
      trackGuide('article_opened', { article_url: message.url });
      openGuideExternalUrl(message.url);
      return;
    }

    if (message.type === GUIDE_EMBED_GO_BACK_MESSAGE_TYPE) {
      navigateGuideFrame(message.fallbackUrl);
    }
  });
};

const readExpandedPreference = () => {
  try {
    return Boolean(window.localStorage.getItem(GUIDE_WIDTH_STORAGE_KEY));
  } catch (error) {
    // localStorage puo' essere negato: la preferenza e' un extra.
    return false;
  }
};

const syncResizeButton = () => {
  const isExpanded = $('body').hasClass(EXPANDED_CLASS);
  const label = isExpanded ? 'Riduci il pannello' : 'Allarga il pannello';

  $(`#${PANEL_ID} [data-guide-resize]`)
    .attr('aria-label', label)
    .attr('title', label)
    .attr('aria-pressed', isExpanded ? 'true' : 'false')
    .html(isExpanded ? '&raquo;' : '&laquo;');
};

export const toggleGuidePanelWidth = () => {
  const isExpanded = !$('body').hasClass(EXPANDED_CLASS);
  $('body').toggleClass(EXPANDED_CLASS, isExpanded);
  syncResizeButton();

  try {
    if (isExpanded) {
      window.localStorage.setItem(GUIDE_WIDTH_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(GUIDE_WIDTH_STORAGE_KEY);
    }
  } catch (error) {
    // Preferenza non salvabile: il pannello resta comunque allargato.
  }
};

const ensureGuidePanel = () => {
  if ($(`#${PANEL_ID}`).length) return;

  const title = getGuideTitle();
  $('body').append(`
    <aside id="${PANEL_ID}" class="guide-panel" role="complementary" aria-label="${title}">
      <div class="guide-panel__header">
        <h2 class="guide-panel__title">${title}</h2>
        <div class="guide-panel__actions">
          <button type="button" class="guide-panel__action" data-guide-resize aria-label="Allarga il pannello" title="Allarga il pannello">&laquo;</button>
          <button type="button" class="guide-panel__action" data-guide-close aria-label="Chiudi le guide" title="Chiudi le guide">&times;</button>
        </div>
      </div>
      <iframe
        data-guide-frame="true"
        title="${title}"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="clipboard-write; fullscreen"
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
        allowfullscreen
      ></iframe>
    </aside>
  `);

  $(`#${PANEL_ID} iframe[data-guide-frame]`).on('load', function () {
    if (!isGuidePanelOpen()) return;
    trackGuide('loaded');
  });
};

export const openGuidePanel = (surface, embedUrl) => {
  bindGuideMessageListener();
  ensureGuidePanel();

  const targetUrl = normalizeGuideNavigationUrl(embedUrl) || GUIDE_EMBED_URL;
  const $iframe = $(`#${PANEL_ID} iframe[data-guide-frame]`);
  $iframe.data('guideSurface', surface || GUIDE_SURFACE_PRIVATE_NAVBAR);
  if ($iframe.attr('src') !== targetUrl) {
    $iframe.attr('src', targetUrl);
  }

  $('body').toggleClass(EXPANDED_CLASS, readExpandedPreference());
  syncResizeButton();
  $('body').addClass(OPEN_CLASS);
  window.__giottoGuideOpenedAt = Date.now();
  trackGuide('opened');
};

export const closeGuidePanel = () => {
  if (!isGuidePanelOpen()) return;

  const openedAt = window.__giottoGuideOpenedAt;
  trackGuide('closed', openedAt ? { duration_ms: Date.now() - openedAt } : {});
  window.__giottoGuideOpenedAt = null;
  $('body').removeClass(OPEN_CLASS);
};

export const toggleGuidePanel = (surface, embedUrl) => {
  if (isGuidePanelOpen()) {
    closeGuidePanel();
    return;
  }

  openGuidePanel(surface, embedUrl);
};
